"use client";

// Ce que voit quelqu'un qui n'est pas sur iPhone après avoir cliqué sur le
// lien d'installation de Reps.
//
// Trois publics arrivent ici, et chacun doit repartir avec un chemin :
// - Android : la version n'existe pas, il laisse son adresse. C'est la seule
//   façon de savoir si Android vaut l'investissement, au lieu de le deviner.
// - iPad ou iOS non détecté (depuis iPadOS 13, un iPad se présente comme un
//   Mac côté serveur) : le badge App Store est là, sous ses yeux.
// - Ordinateur : il repart vers /reps, qui fonctionne dans le navigateur.
//
// La liste d'attente réutilise l'API des formations à venir avec un identifiant
// dédié. Le contrôleur accepte n'importe quel identifiant, gère déjà membre et
// visiteur, l'unicité, et prévient l'équipe par e-mail : il n'y avait aucune
// raison d'écrire un second système pour stocker des adresses.
//
// Tutoiement assumé : tout l'univers Reps est à la voix de BeBot.

import { useEffect, useState } from "react";
import Image from "next/image";
import { useSession } from "next-auth/react";
import { useLocale, useTranslations } from "next-intl";
import { motion } from "motion/react";
import { Link } from "@/i18n/navigation";
import Button from "@/components/ui/Button/Button";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import { appStoreBadge } from "@/lib/reps/mobileApp";
import { readLocal, writeLocal } from "@/helpers/safeStorage";

// Identifiant de la liste d'attente. Il ne désigne aucune formation : c'est
// une étiquette, et le back n'en demande pas plus.
const WAITLIST_SLUG = "reps-android";
const STORAGE_KEY = "believemy_waitlist_reps-android";

export default function RepsAppGate({ appStoreUrl, source }) {
    const t = useTranslations();
    const locale = useLocale();
    const { data: session, status } = useSession();

    const [email, setEmail] = useState("");
    const [state, setState] = useState("idle"); // idle | loading | error
    const [errorMessage, setErrorMessage] = useState(null);
    const [joined, setJoined] = useState(false);

    const isMember = Boolean(session?.user);
    const badge = appStoreBadge(locale);

    // Un visiteur n'est identifiable par aucun serveur : son navigateur est la
    // seule mémoire disponible pour ne pas lui reproposer le formulaire.
    useEffect(() => {
        if (status === "loading") return;
        if (readLocal(STORAGE_KEY)) setJoined(true);
    }, [status]);

    async function handleSubmit(event) {
        event.preventDefault();
        if (state === "loading") return;

        if (!isMember && !email.trim()) {
            setState("error");
            setErrorMessage(t("reps_install_android_error_email"));
            return;
        }

        setState("loading");
        setErrorMessage(null);

        try {
            const response = await fetch("/api/course-waitlist/join", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    courseSlug: WAITLIST_SLUG,
                    // La source voyage dans le libellé : c'est lui que
                    // l'e-mail d'alerte affiche, et ça évite de toucher à une
                    // API partagée avec les formations.
                    courseName: source
                        ? `Reps pour Android (via ${source})`
                        : "Reps pour Android",
                    email: isMember ? null : email.trim(),
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                setState("error");
                setErrorMessage(data?.message || t("reps_install_android_error"));
                return;
            }

            // Déjà inscrit n'est pas une erreur : même remerciement, et on ne
            // révèle jamais qu'une adresse est connue de la plateforme.
            writeLocal(STORAGE_KEY, new Date().toISOString());
            setJoined(true);
            setState("idle");
        } catch (error) {
            setState("error");
            setErrorMessage(t("reps_install_android_error"));
        }
    }

    return (
        <div className="mx-auto flex min-h-screen max-w-lg flex-col justify-center px-5 py-16">
            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, ease: "easeOut" }}
                className="flex flex-col gap-8"
            >
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white md:text-3xl">
                        {t("reps_install_title")}
                    </h1>
                    <p className="mx-auto mt-3 mb-0 max-w-sm text-sm leading-relaxed text-gray-500 dark:text-grayDark-400">
                        {t("reps_install_subtitle")}
                    </p>
                </div>

                {/* iPhone non détecté, iPad, ou ordinateur : le chemin direct
                    reste visible plutôt que d'être deviné. */}
                {appStoreUrl && (
                    <div className="flex flex-col items-center gap-3 rounded-3xl border border-gray-100 bg-white p-6 text-center dark:border-white/10 dark:bg-white/[0.03]">
                        <p className="mb-0 text-sm font-semibold text-gray-900 dark:text-white">
                            {t("reps_install_ios_title")}
                        </p>
                        <a
                            href={appStoreUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <Image
                                src={badge.black}
                                width={badge.width}
                                height={badge.height}
                                alt="App Store"
                                className="dark:hidden"
                            />
                            <Image
                                src={badge.white}
                                width={badge.width}
                                height={badge.height}
                                alt="App Store"
                                className="hidden dark:block"
                            />
                        </a>
                    </div>
                )}

                {/* Android : la demande se mesure au lieu de se perdre. */}
                <div className="rounded-3xl border border-gray-100 bg-white p-6 dark:border-white/10 dark:bg-white/[0.03]">
                    {joined ? (
                        <div className="flex flex-col items-center gap-2 text-center">
                            <SolarIcon
                                name="check-circle-bold"
                                className="text-2xl text-green-600 dark:text-green-500"
                            />
                            <p className="mb-0 text-sm font-semibold text-gray-900 dark:text-white">
                                {t("reps_install_android_joined_title")}
                            </p>
                            <p className="mb-0 text-sm leading-relaxed text-gray-500 dark:text-grayDark-400">
                                {t("reps_install_android_joined_desc")}
                            </p>
                        </div>
                    ) : (
                        <form
                            onSubmit={handleSubmit}
                            className="flex flex-col gap-3"
                        >
                            <div className="flex items-center gap-2">
                                <SolarIcon
                                    name="smartphone-linear"
                                    className="text-lg text-gray-400"
                                />
                                <p className="mb-0 text-sm font-semibold text-gray-900 dark:text-white">
                                    {t("reps_install_android_title")}
                                </p>
                            </div>
                            <p className="mb-0 text-sm leading-relaxed text-gray-500 dark:text-grayDark-400">
                                {isMember
                                    ? t("reps_install_android_desc_member")
                                    : t("reps_install_android_desc")}
                            </p>

                            {!isMember && (
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder={t(
                                        "reps_install_android_placeholder",
                                    )}
                                    autoComplete="email"
                                    className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 outline-hidden transition-colors focus:border-gray-400 dark:border-white/10 dark:bg-white/5 dark:text-white dark:focus:border-white/30"
                                />
                            )}

                            {errorMessage && (
                                <p className="mb-0 text-sm text-red-600 dark:text-red-400">
                                    {errorMessage}
                                </p>
                            )}

                            <Button
                                htmlType="submit"
                                type="primary"
                                isLoading={state === "loading"}
                                progress
                            >
                                {t("reps_install_android_cta")}
                            </Button>
                        </form>
                    )}
                </div>

                {/* Rien ne bloque : Reps tourne déjà dans le navigateur. */}
                <div className="text-center">
                    <p className="mb-2 text-sm text-gray-500 dark:text-grayDark-400">
                        {t("reps_install_web_note")}
                    </p>
                    <Link
                        href="/reps"
                        className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-900 hover:underline dark:text-white"
                    >
                        {t("reps_install_web_cta")}
                        <SolarIcon
                            name="alt-arrow-right-linear"
                            className="text-base"
                        />
                    </Link>
                </div>
            </motion.div>
        </div>
    );
}
